import { useHasPermission } from '@versita/frontend/auth-web';
import {
  ButtonSize,
  ButtonStyle,
  ConstrainedLayout,
  Losenge,
  Notice,
  TimetableItemProps,
} from '@versita/frontend/campus-ui-web';
import { useStudentTimetablePageTitle } from '@versita/frontend/timetable-hooks';
import {
  setIsCurrentPersonalTimetableAvailable,
  setIsRedirectedToCourseTimetable,
  setTimetableId,
} from '@versita/frontend/timetable-state';
import { Actions } from '@versita/shared/models';
import { FC, useEffect, useMemo } from 'react';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import environment from '../../environments/environment';
import { useUserWidget } from '../../hooks/useUserWidget';
import { CodeShareWarning } from '../../molecules/CodeShareWarning';
import { TimetableView } from '../../organisms/TimetableView';
import {
  ListStudentEventsApiResponse,
  useGetApiTokenQuery,
} from '../../services/generated/studentPortalApi';
import telemetry from '../../services/telemetry';
import { useAppDispatch, useTimetableState } from '../../state';
import { NotRegisteredPage } from '../NotRegisteredPage';
import { useStudentEvents } from './hooks/useStudentEvents';
import * as Sentry from '@sentry/react';
import {
  StyleDesktopTimetableHeaderButton,
  StyledCourseTimetableSubheading,
  StyledMobileTimetableHeaderButton,
} from '../CourseTimetable/CourseTimetable.styles';

export type CourseTimetablePageParams = {
  courseId: string;
  courseYear: string;
};

/**
 * Page showing the timetable for the logged-in student or the student
 * specified by the networkId URL param.
 *
 * This page can be accessed by both students and admins via different routes,
 * the presences of the networkId param is used which is the case.
 */
export const StudentTimetable = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nordr = searchParams.has('nordr');
  const canReadAllTimetables = useHasPermission(Actions.Timetable.Read.All);
  const timetableState = useTimetableState();
  const { data: token } = useGetApiTokenQuery(undefined, {
    skip: !timetableState.icsUrlModalVisible,
  });
  const icsUrl = token
    ? `${environment.apis.studentPortalApi.endpoint}/events/timetable/student/ics?t=${token.value}`
    : 'Loading...';
  const { networkId } = useParams();
  const isAdminView = useMemo(() => !!networkId, [networkId]);
  const canViewUnpublished = isAdminView && canReadAllTimetables;
  const {
    timetableItems,
    timetable,
    registered: isRegistered,
    isFetching,
    isLoading,
  } = useStudentEvents({
    weekStart: timetableState.timetableWeekStart,
    networkId: networkId || '',
  });
  const isPublished = !!timetable?.published;

  useEffect(() => {
    if (!isLoading && timetable) {
      telemetry.trackPageView({
        name: isAdminView
          ? 'AdminStudentTimetable'
          : isRegistered
            ? 'StudentTimetable'
            : 'NotRegisteredPage',
        properties: {
          networkId,
          isRegistered,
          isPublished,
        },
      });
      Sentry.setContext('timetable', {
        type: networkId ? 'Admin' : 'Student',
        courseId: timetable?.courseId,
        courseYear: timetable?.courseYear,
        student: networkId || timetable?.studentName,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminView, networkId, isRegistered, timetable, isPublished]);

  useEffect(() => {
    dispatch(setTimetableId(networkId || 'ME'));
  }, [networkId, dispatch]);

  // Students shouldn't be able to view unpublished timetables.
  // We also need to handle unregistered students who won't have timetable data.

  if (
    isFetching ||
    isPublished ||
    canViewUnpublished ||
    (nordr && timetable.isCurrentAvailable)
  ) {
    return (
      <StudentTimetableView
        timetable={timetable}
        timetableItems={timetableItems}
        isAdminView={isAdminView}
        icsUrl={icsUrl}
        isLoading={isLoading}
        isFetching={isFetching}
      />
    );
  }

  if (!isRegistered) {
    return <NotRegisteredPage />;
  }

  // timetable unpublished or error
  dispatch(setIsRedirectedToCourseTimetable(true));
  dispatch(
    setIsCurrentPersonalTimetableAvailable(timetable.isCurrentAvailable),
  );
  navigate(
    `/course/${timetable.redirectTo.courseId}/${timetable.redirectTo.courseYear}`,
  );
  return null;
};

interface StudentTimetableViewProps {
  isAdminView: boolean;
  isLoading: boolean;
  isFetching: boolean;
  icsUrl: string;
  timetable: ListStudentEventsApiResponse['timetable'];
  timetableItems: Record<string, TimetableItemProps[]>;
}

const StudentTimetableView: FC<StudentTimetableViewProps> = ({
  isAdminView,
  timetable,
  isLoading,
  icsUrl,
  isFetching,
  timetableItems,
}) => {
  const userWidgetProps = useUserWidget();
  const pageTitle = useStudentTimetablePageTitle(isAdminView, timetable);
  return (
    <>
      <CodeShareWarning />
      <ConstrainedLayout
        userWidget={userWidgetProps}
        heading={{
          title: (
            <>
              {pageTitle}{' '}
              {!isAdminView && (
                <StyledMobileTimetableHeaderButton
                  to={`/course/${timetable?.courseId}/${timetable?.courseYear}?hccb`}
                  $buttonStyle={ButtonStyle.Secondary}
                  $buttonSize={ButtonSize.Small}
                  $fullWidth
                >
                  Go to Course Timetable
                </StyledMobileTimetableHeaderButton>
              )}
            </>
          ),
          subtitle: (
            <>
              <div>
                {isAdminView && (
                  <Losenge
                    status={timetable?.published ? 'success' : 'warning'}
                    style={{ marginBottom: 16 }}
                  >
                    {timetable?.published ? 'Published' : 'Unpublished'}
                  </Losenge>
                )}
              </div>
              <StyledCourseTimetableSubheading>
                {isAdminView
                  ? 'Student Timetable (Admin View)'
                  : 'My Timetable'}
                {!isAdminView && !!timetable && (
                  <StyleDesktopTimetableHeaderButton
                    $buttonStyle={ButtonStyle.Secondary}
                    $buttonSize={ButtonSize.Small}
                    to={`/course/${timetable?.courseId}/${timetable?.courseYear}?hccb`}
                  >
                    Go to course timetable
                  </StyleDesktopTimetableHeaderButton>
                )}
              </StyledCourseTimetableSubheading>
            </>
          ),
          loading: !pageTitle,
        }}
      >
        {!isLoading && isAdminView && (
          <p style={{ marginTop: '-4rem' }}>
            <strong>Course Name:</strong> {timetable?.courseName}
            <br />
            <strong>Course ID:</strong>{' '}
            {timetable?.courseId?.replace(/\./g, '/')}
            <br />
            <strong>Course Year:</strong> {timetable?.courseYear}
            <br />
            <strong>Next Course ID:</strong>{' '}
            {timetable?.nextCourseId?.replace(/\./g, '/') || 'None'}
            <br />
            <strong>Next Course Year:</strong>{' '}
            {timetable?.nextCourseYear || 'None'}
          </p>
        )}
        {!isLoading && isAdminView && !timetable?.published && (
          <Notice title="This timetable is unpublished.">
            This timetable is not visible to students. When{' '}
            {timetable?.studentName} logs in, they will be redirected to their{' '}
            <Link
              to={`/course/${timetable?.redirectTo?.courseId}/${timetable?.redirectTo?.courseYear}`}
            >
              Course Timetable.
            </Link>{' '}
          </Notice>
        )}
        {!isLoading && (
          <TimetableView
            timetableItems={timetableItems}
            loading={isFetching}
            isAdminView={isAdminView}
            icsUrl={icsUrl}
          />
        )}
      </ConstrainedLayout>
    </>
  );
};

export default StudentTimetable;
